import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ErrorToast } from "../../toast/Toasts";
import {
  getWalletBalance,
  getWithdrawlRequest,
  insertWithdrawlRequest,
  updateWithdrawlRequest,
} from "./services/wallet.services";
import { clearWalletIndex, clearwithdrawRequestDetails } from "./walletSlice";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router";

const WalletWithdrawalForm = ({ ID = null, setIsEdit }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const withdrawRequestDetails = useAppSelector(
    (state) => state.wallet.withdrawRequestDetails
  );

  const validationSchema = Yup.object().shape({
    Amount: Yup.number()
      .required("Amount is required")
      .min(1, "Minimum withdrawal amount is ₹1")
      // .min(100, "Minimum withdrawal amount is ₹100")
      .max(50000, "Maximum withdrawal amount is ₹50,000"),
    PaymentType: Yup.string()
      .required("Withdrawal method is required")
      .oneOf(["Bank", "UPI"], "Invalid withdrawal method"),
    AccountNo: Yup.string().when("PaymentType", {
      is: "Bank",
      then: (schema) =>
        schema
          .required("Account number is required")
          .matches(/^\d{9,18}$/, "Invalid account number"),
    }),
    IFSCCode: Yup.string().when("PaymentType", {
      is: "Bank",
      then: (schema) =>
        schema
          .required("IFSC code is required")
          .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code"),
    }),
    BankName: Yup.string().when("PaymentType", {
      is: "Bank",
      then: (schema) => schema.required("Bank name is required"),
    }),
    BranchName: Yup.string().when("PaymentType", {
      is: "Bank",
      then: (schema) => schema.required("Branch name is required"),
    }),
    UPIID: Yup.string().when("PaymentType", {
      is: "UPI",
      then: (schema) =>
        schema
          .required("UPI ID is required")
          .matches(
            /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/,
            "Invalid UPI ID (e.g., name@upi)"
          ),
    }),
    // CreditCardNo: Yup.string().when("PaymentType", {
    //   is: "Cash",
    //   then: (schema) =>
    //     schema
    //       .required("Credit card number is required")
    //       .matches(/^\d{12,19}$/, "Invalid credit card number"),
    // }),
    Remarks: Yup.string().max(500, "Remarks cannot exceed 500 characters"),
  });

  const formik = useFormik({
    initialValues: ID
      ? {
          Amount: withdrawRequestDetails?.Amount || "",
          PaymentType: withdrawRequestDetails?.PaymentType || "",
          AccountNo: withdrawRequestDetails?.AccountNo || "",
          IFSCCode: withdrawRequestDetails?.IFSCCode || "",
          BankName: withdrawRequestDetails?.BankName || "",
          BranchName: withdrawRequestDetails?.BranchName || "",
          UPIID: withdrawRequestDetails?.UPIID || "",
          Remarks: withdrawRequestDetails?.Remarks || "",
        }
      : {
          Amount: "",
          PaymentType: "",
          AccountNo: "",
          IFSCCode: "",
          BankName: "",
          BranchName: "",
          UPIID: "",
          //   CreditCardNo: "",
          Remarks: "",
        },

    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          UserID: localStorage.getItem("user_id"),
          Amount: values.Amount,
          PaymentType: values.PaymentType,
          Remarks: values.Remarks,
          ...(values.PaymentType === "Bank"
            ? {
                AccountNo: values.AccountNo,
                IFSCCode: values.IFSCCode,
                BankName: values.BankName,
                BranchName: values.BranchName,
              }
            : values.PaymentType === "UPI"
            ? { UPIID: values.UPIID }
            : {}),
          // : { CreditCardNo: values.CreditCardNo }),
        };

        if (ID) {
          const response: any = await dispatch(
            updateWithdrawlRequest({ ...payload, ID: ID })
          );

          if (response?.meta.requestStatus === "fulfilled") {
            dispatch(getWalletBalance());
            resetForm();
            setIsEdit(false);
            dispatch(getWithdrawlRequest(ID));
          } else {
            ErrorToast(
              response.payload?.Message || "Failed to submit withdrawal request"
            );
          }
        } else {
          const response: any = await dispatch(insertWithdrawlRequest(payload));

          if (response?.meta.requestStatus === "fulfilled") {
            dispatch(getWalletBalance());
            resetForm();
            dispatch(clearWalletIndex());
          } else {
            ErrorToast(
              response.payload?.Message || "Failed to submit withdrawal request"
            );
          }
        }
      } catch (error) {
        console.error("Withdrawal failed:", error);
        ErrorToast("An error occurred. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="row mt-3">
      <div className="col-12 wallet-main-card">
        <div className="d-flex justify-content-between">
          <h4>Withdrawal Request</h4>
          {!ID && (
            <IoMdCloseCircleOutline
              className="fs-3 cursor-pointer"
              onClick={() => dispatch(clearWalletIndex())}
            />
          )}
        </div>
        <div className="mt-4">
          <form onSubmit={formik.handleSubmit}>
            {/* Withdrawal Amount */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="field">
                  <label
                    className={
                      formik.touched.Amount && formik.errors.Amount
                        ? "error-label fs-6"
                        : "fs-6"
                    }
                  >
                    Withdrawal Amount (₹) <code>*</code>
                  </label>
                  <input
                    type="number"
                    name="Amount"
                    className={`form-control ${
                      formik.touched.Amount && formik.errors.Amount
                        ? "error-border"
                        : ""
                    }`}
                    placeholder="Enter amount"
                    value={formik.values.Amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.Amount && formik.errors.Amount && (
                    <div className="error-label">
                      {formik.errors.Amount as string}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Type */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="field">
                  <label
                    className={
                      formik.touched.PaymentType && formik.errors.PaymentType
                        ? "error-label fs-6"
                        : "fs-6"
                    }
                  >
                    Withdrawal Method <code>*</code>
                  </label>
                  <select
                    name="PaymentType"
                    className={`form-select ${
                      formik.touched.PaymentType && formik.errors.PaymentType
                        ? "error-border"
                        : ""
                    }`}
                    value={formik.values.PaymentType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select method</option>
                    <option value="Bank">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    {/* <option value="Cash">Cash</option> */}
                  </select>
                  {formik.touched.PaymentType && formik.errors.PaymentType && (
                    <div className="error-label">
                      {formik.errors.PaymentType as string}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bank Details (conditionally shown) */}
            {formik.values.PaymentType === "Bank" && (
              <>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="field">
                      <label
                        className={
                          formik.touched.AccountNo && formik.errors.AccountNo
                            ? "error-label fs-6"
                            : "fs-6"
                        }
                      >
                        Account Number <code>*</code>
                      </label>
                      <input
                        type="text"
                        name="AccountNo"
                        className={`form-control ${
                          formik.touched.AccountNo && formik.errors.AccountNo
                            ? "error-border"
                            : ""
                        }`}
                        placeholder="Enter account number"
                        value={formik.values.AccountNo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.AccountNo && formik.errors.AccountNo && (
                        <div className="error-label">
                          {formik.errors.AccountNo as string}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="field">
                      <label
                        className={
                          formik.touched.IFSCCode && formik.errors.IFSCCode
                            ? "error-label fs-6"
                            : "fs-6"
                        }
                      >
                        IFSC Code <code>*</code>
                      </label>
                      <input
                        type="text"
                        name="IFSCCode"
                        className={`form-control ${
                          formik.touched.IFSCCode && formik.errors.IFSCCode
                            ? "error-border"
                            : ""
                        }`}
                        placeholder="Enter IFSC code"
                        value={formik.values.IFSCCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.IFSCCode && formik.errors.IFSCCode && (
                        <div className="error-label">
                          {formik.errors.IFSCCode as string}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="field">
                      <label
                        className={
                          formik.touched.BankName && formik.errors.BankName
                            ? "error-label fs-6"
                            : "fs-6"
                        }
                      >
                        Bank Name <code>*</code>
                      </label>
                      <input
                        type="text"
                        name="BankName"
                        className={`form-control ${
                          formik.touched.BankName && formik.errors.BankName
                            ? "error-border"
                            : ""
                        }`}
                        placeholder="Enter bank name"
                        value={formik.values.BankName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.BankName && formik.errors.BankName && (
                        <div className="error-label">
                          {formik.errors.BankName as string}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="field">
                      <label
                        className={
                          formik.touched.BranchName && formik.errors.BranchName
                            ? "error-label fs-6"
                            : "fs-6"
                        }
                      >
                        Branch Name <code>*</code>
                      </label>
                      <input
                        type="text"
                        name="BranchName"
                        className={`form-control ${
                          formik.touched.BranchName && formik.errors.BranchName
                            ? "error-border"
                            : ""
                        }`}
                        placeholder="Enter branch name"
                        value={formik.values.BranchName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.BranchName &&
                        formik.errors.BranchName && (
                          <div className="error-label">
                            {formik.errors.BranchName as string}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* UPI Details (conditionally shown) */}
            {formik.values.PaymentType === "UPI" && (
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="field">
                    <label
                      className={
                        formik.touched.UPIID && formik.errors.UPIID
                          ? "error-label fs-6"
                          : "fs-6"
                      }
                    >
                      UPI ID <code>*</code>
                    </label>
                    <input
                      type="text"
                      name="UPIID"
                      className={`form-control ${
                        formik.touched.UPIID && formik.errors.UPIID
                          ? "error-border"
                          : ""
                      }`}
                      placeholder="Enter UPI ID (e.g., name@upi)"
                      value={formik.values.UPIID}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.UPIID && formik.errors.UPIID && (
                      <div className="error-label">
                        {formik.errors.UPIID as string}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cash/Credit Card Details (conditionally shown) */}
            {/* {formik.values.PaymentType === "Cash" && (
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="field">
                    <label
                      className={
                        formik.touched.CreditCardNo &&
                        formik.errors.CreditCardNo
                          ? "error-label fs-6"
                          : "fs-6"
                      }
                    >
                      Credit Card Number <code>*</code>
                    </label>
                    <input
                      type="text"
                      name="CreditCardNo"
                      className={`form-control ${
                        formik.touched.CreditCardNo &&
                        formik.errors.CreditCardNo
                          ? "error-border"
                          : ""
                      }`}
                      placeholder="Enter credit card number"
                      value={formik.values.CreditCardNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.CreditCardNo &&
                      formik.errors.CreditCardNo && (
                        <div className="error-label">
                          {formik.errors.CreditCardNo}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )} */}

            {/* Remarks */}
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="field">
                  <label className="fs-6">Remarks</label>
                  <textarea
                    name="Remarks"
                    className={`form-control ${
                      formik.touched.Remarks && formik.errors.Remarks
                        ? "error-border"
                        : ""
                    }`}
                    placeholder="Enter remarks (optional)"
                    rows={3}
                    value={formik.values.Remarks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.Remarks && formik.errors.Remarks && (
                    <div className="error-label">
                      {formik.errors.Remarks as string}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="row  mt-4">
              <div className="col-md-6 d-flex gap-3">
                <button
                  type="submit"
                  className="fill-btn manage-btn"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
                <button
                  type="button"
                  className="btn wallet-cancel-btn"
                  onClick={() => {
                    formik.resetForm();
                    if (ID && setIsEdit) setIsEdit(false);
                  }}
                >
                  Clear
                </button>
              </div>
              {!ID && (
                <div className="col-md-6 justify-content-end d-flex align-items-center mt-3 mt-md-0">
                  <span className="fw-600 fs-6">
                    <a
                      href="javascript:void(0)"
                      className="text-decoration-underline"
                      onClick={() => navigate("/walletwithdrawalhistory")}
                    >
                      View Withdrawal Requests
                    </a>
                  </span>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WalletWithdrawalForm;
